/**
 * MovementController - Handles player movement, camera control, and aiming
 */
import * as THREE from 'three';

export class MovementController {
    constructor(player, camera, scene, options = {}) {
        this.player = player;
        this.camera = camera;
        this.scene = scene;
        this.socket = options.socket || null;
        
        // Enable/disable movement control
        this.enabled = true;
        
        // Camera settings
        this.cameraOffset = new THREE.Vector3(0, 5, 7); // Camera offset from player
        this.cameraFollowSpeed = 0.05; // How fast camera follows player
        this.cameraLookAtOffset = new THREE.Vector3(0, 1, 0); // Look at player's head
        this._tempLookAtTarget = null; // Temporary target for smooth camera look
        this._targetCameraPosition = new THREE.Vector3(); // Target camera position for smooth movement
        this._currentCameraOffset = this.cameraOffset.clone(); // Current camera offset for smooth transitions
        
        // Ground plane for raycasting
        this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        this.raycaster = new THREE.Raycaster();
        this.pointerPosition = new THREE.Vector2();
        this.targetPoint = new THREE.Vector3();
        this.targetRotation = new THREE.Euler();
        
        // Mouse/touch controls
        this.isPointerDown = false;
        this.previousPointerPosition = new THREE.Vector2();
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // Movement state
        this.movementDirection = new THREE.Vector3();
        this.previousMovementDirection = new THREE.Vector3(); // Track previous movement direction
        this.rotationSpeed = 15; // Increased from 5 to 15 for faster rotation
        this.maxMovementSpeed = 5; // Max units per second
        
        // Mobile rotation control
        this.mobileRotationSpeed = 0.05; // Speed of rotation when using joystick
        this.isRotating = false; // Track if we're currently rotating
        
        // Input state
        this.keys = {
            forward: false,  // W
            backward: false, // S
            left: false,     // A
            right: false,    // D
            shift: false     // Shift (sprint)
        };
        
        // Touch controls for mobile
        this.touchControls = {
            move: {
                active: false,
                touchId: null,
                startX: 0,
                startY: 0,
                currentX: 0,
                currentY: 0
            },
            rotate: {
                active: false,
                touchId: null,
                startX: 0,
                currentX: 0,
                sensitivity: 0.01, // Same as mouse sensitivity
                lastX: 0
            }
        };
        
        // Sync data
        this.lastSyncTime = Date.now();
        this.syncRate = 50; // ms between position updates (20 per second) to match server
        this.positionBuffer = []; // Buffer for position interpolation
        this.maxBufferSize = 3; // Number of positions to buffer
        
        // Add new properties for movement tracking
        this.hasRotated = false;
        
        // Initialize
        this._setupEventListeners();
    }
    
    /**
     * Set up event listeners for mouse, keyboard, and touch
     * @private
     */
    _setupEventListeners() {
        // Desktop controls
        document.addEventListener('keydown', (e) => this._handleKeyDown(e));
        document.addEventListener('keyup', (e) => this._handleKeyUp(e));
        document.addEventListener('mousemove', (e) => this._handleMouseMove(e));
        document.addEventListener('mousedown', () => { this.isPointerDown = true; });
        document.addEventListener('mouseup', () => { this.isPointerDown = false; });
        
        // Mobile controls
        if (this.isMobile) {
            document.addEventListener('touchstart', (e) => this._handleTouchStart(e), { passive: false });
            document.addEventListener('touchmove', (e) => this._handleTouchMove(e), { passive: false });
            document.addEventListener('touchend', (e) => this._handleTouchEnd(e));
        }
    }
    
    /**
     * Handle key down events for movement
     * @param {KeyboardEvent} event - Key down event
     * @private
     */
    _handleKeyDown(event) {
        if (!this.enabled || event.repeat) return; // Ignore repeats and if disabled
        
        // Update key state
        switch (event.key.toLowerCase()) {
            case 'w': this.keys.forward = true; break;
            case 's': this.keys.backward = true; break;
            case 'a': this.keys.left = true; break;
            case 'd': this.keys.right = true; break;
            case 'shift': this.keys.shift = true; break;
        }
    }
    
    /**
     * Handle key up events for movement
     * @param {KeyboardEvent} event - Key up event
     * @private
     */
    _handleKeyUp(event) {
        if (!this.enabled) return; // Ignore if disabled
        
        // Update key state
        switch (event.key.toLowerCase()) {
            case 'w': this.keys.forward = false; break;
            case 's': this.keys.backward = false; break;
            case 'a': this.keys.left = false; break;
            case 'd': this.keys.right = false; break;
            case 'shift': this.keys.shift = false; break;
        }
    }
    
    /**
     * Handle mouse movement for aiming
     * @param {MouseEvent} event 
     * @private
     */
    _handleMouseMove(event) {
        if (!this.enabled || !this.camera || !this.scene) return;
        
        // Calculate normalized device coordinates
        this.pointerPosition.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.pointerPosition.y = -(event.clientY / window.innerHeight) * 2 + 1;
        
        // Update target point for player to face
        this._updateTargetPoint();
    }
    
    /**
     * Handle touch start events
     * @param {TouchEvent} event 
     * @private
     */
    _handleTouchStart(event) {
        if (!this.enabled) return;
        
        event.preventDefault();
        
        // Get the joystick element
        const joystickOuter = document.querySelector('.joystick-outer');
        if (!joystickOuter) return;
        
        const joystickRect = joystickOuter.getBoundingClientRect();
        
        // Check if touch is within joystick area
        for (const touch of event.changedTouches) {
            const x = touch.clientX;
            const y = touch.clientY;
            
            // Check if touch is within joystick area
            if (x >= joystickRect.left && x <= joystickRect.right &&
                y >= joystickRect.top && y <= joystickRect.bottom) {
                // Initialize movement control if not already active
                if (!this.touchControls.move.active) {
                    this.touchControls.move.active = true;
                    this.touchControls.move.touchId = touch.identifier;
                    this.touchControls.move.startX = x;
                    this.touchControls.move.startY = y;
                    this.touchControls.move.currentX = x;
                    this.touchControls.move.currentY = y;
                }
            }
        }
    }
    
    /**
     * Handle touch move events
     * @param {TouchEvent} event 
     * @private
     */
    _handleTouchMove(event) {
        if (!this.enabled) return;
        
        event.preventDefault();
        
        for (const touch of event.changedTouches) {
            const x = touch.clientX;
            const y = touch.clientY;
            
            // Update movement control if this is the active touch
            if (this.touchControls.move.active && touch.identifier === this.touchControls.move.touchId) {
                this.touchControls.move.currentX = x;
                this.touchControls.move.currentY = y;

                // Update joystick visual position
                const joystickInner = document.querySelector('.joystick-inner');
                if (joystickInner) {
                    const joystickOuter = document.querySelector('.joystick-outer');
                    const outerRect = joystickOuter.getBoundingClientRect();
                    const outerCenterX = outerRect.left + outerRect.width / 2;
                    const outerCenterY = outerRect.top + outerRect.height / 2;
                    
                    // Calculate distance from center
                    const deltaX = x - outerCenterX;
                    const deltaY = y - outerCenterY;
                    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                    const maxRadius = outerRect.width / 2 - joystickInner.offsetWidth / 2;
                    
                    // Normalize and limit the distance
                    const normalizedDistance = Math.min(distance, maxRadius);
                    const angle = Math.atan2(deltaY, deltaX);
                    
                    // Calculate new position
                    const newX = Math.cos(angle) * normalizedDistance;
                    const newY = Math.sin(angle) * normalizedDistance;
                    
                    // Apply the new position
                    joystickInner.style.transform = `translate(${newX}px, ${newY}px)`;
                }
            }
        }
    }
    
    /**
     * Handle touch end events
     * @param {TouchEvent} event 
     * @private
     */
    _handleTouchEnd(event) {
        for (const touch of event.changedTouches) {
            // Reset the appropriate control if this is the active touch
            if (this.touchControls.move.active && touch.identifier === this.touchControls.move.touchId) {
                this.touchControls.move.active = false;
                this.touchControls.move.touchId = null;
                
                // Reset joystick position
                const joystickInner = document.querySelector('.joystick-inner');
                if (joystickInner) {
                    joystickInner.style.transform = 'translate(0, 0)';
                }
            }
        }
    }
    
    /**
     * Calculate target point on ground for player to face
     * @private
     */
    _updateTargetPoint() {
        if (!this.camera || !this.player) return;
        
        // Create ray from camera through pointer position
        this.raycaster.setFromCamera(this.pointerPosition, this.camera);
        
        // Calculate intersection with ground plane
        const ray = this.raycaster.ray;
        let intersectionPoint = new THREE.Vector3();
        
        // Try to intersect with ground plane
        if (ray.intersectPlane(this.groundPlane, intersectionPoint)) {
            // Check if the intersection point is too close to the player (cursor on character)
            const distanceToPlayer = intersectionPoint.distanceTo(this.player.position);
            
            // Create vector from camera to player
            const cameraToPlayer = new THREE.Vector3().subVectors(
                this.player.position, 
                this.camera.position
            );
            
            // Create vector from camera to intersection point
            const cameraToIntersection = new THREE.Vector3().subVectors(
                intersectionPoint,
                this.camera.position
            );
            
            // Check if intersection is behind the player relative to the camera
            cameraToPlayer.normalize();
            cameraToIntersection.normalize();
            const dotProduct = cameraToPlayer.dot(cameraToIntersection);
            
            // Create a vector from player to intersection
            const playerToIntersection = new THREE.Vector3().subVectors(
                intersectionPoint,
                this.player.position
            );
            
            // Project this vector onto camera forward direction to see if it's behind
            const cameraForward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
            const projectedDistance = playerToIntersection.dot(cameraForward);
            
            // If the cursor is on character, very close to character, or behind character
            // relative to camera view, don't update the target point
            if (distanceToPlayer < 1.0 || projectedDistance < 0 || dotProduct > 0.98) {
                // Initialize target point if needed
                if (this.targetPoint.length() === 0) {
                    // First time initialization - set a default point in front of player
                    const forward = new THREE.Vector3(0, 0, -1).applyEuler(
                        new THREE.Euler(0, this.player.rotation.y, 0)
                    );
                    this.targetPoint.copy(this.player.position).addScaledVector(forward, 5);
                }
                // Otherwise keep using the previous target point (no update)
            } else {
                // Normal case - update target point to ground intersection
                this.targetPoint.copy(intersectionPoint);
            }
        } else {
            // Ray doesn't intersect ground plane (can happen with extreme camera angles)
            // Keep the same target point instead of changing it abruptly
            if (this.targetPoint.length() === 0) {
                // If we don't have a target point yet, create one in front of the player
                const forward = new THREE.Vector3(0, 0, -1).applyEuler(
                    new THREE.Euler(0, this.player.rotation.y, 0)
                );
                this.targetPoint.copy(this.player.position).addScaledVector(forward, 5);
            }
        }
    }
    
    /**
     * Calculate movement based on input
     * @private
     */
    _calculateMovement() {
        // Store previous movement direction before updating
        this.previousMovementDirection.copy(this.movementDirection);
        
        // Reset movement direction
        this.movementDirection.set(0, 0, 0);
        
        if (this.isMobile) {
            // Mobile controls combining rotation and movement
            if (this.touchControls.move.active) {
                const deltaX = this.touchControls.move.currentX - this.touchControls.move.startX;
                const deltaY = this.touchControls.move.currentY - this.touchControls.move.startY;
                
                const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                const maxRadius = 50;
                
                if (length > 0) {
                    const normalizedX = deltaX / length;
                    const normalizedY = deltaY / length;
                    const strength = Math.min(length / maxRadius, 1.0);
                    
                    // Handle rotation based on X-axis movement (like mouse)
                    // Apply rotation in the correct direction regardless of movement
                    const rotationAmount = normalizedX * this.rotationSpeed * 0.01; // Scale down for smoother rotation
                    this.player.rotation.y -= rotationAmount; // Changed from += to -= to fix inversion
                    this.player.modelMesh.rotation.y -= rotationAmount;
                    
                    // Handle movement based on joystick position
                    if (normalizedY < -0.5) this.movementDirection.z -= 1; // Forward
                    if (normalizedY > 0.5) this.movementDirection.z += 1;  // Backward
                    
                    // Set sprint state based on joystick strength
                    this.keys.shift = strength > 0.8;
                    
                    // Normalize movement direction
                    if (this.movementDirection.length() > 0) {
                        this.movementDirection.normalize();
                    }
                }
            }
        } else {
            // Desktop controls
            if (this.keys.forward) this.movementDirection.z -= 1;
            if (this.keys.backward) this.movementDirection.z += 1;
            if (this.keys.left) this.movementDirection.x -= 1;
            if (this.keys.right) this.movementDirection.x += 1;
            
            if (this.movementDirection.length() > 0) {
                this.movementDirection.normalize();
            }
        }
    }
    
    /**
     * Update player rotation to face target
     * @param {number} deltaTime - Time since last update in seconds
     * @private
     */
    _updatePlayerRotation(deltaTime) {
        if (!this.player || !this.player.modelMesh) return;

        if (this.isMobile) {
            // For mobile, we're handling rotation directly in _calculateMovement
            // based on joystick X-axis movement
            return;
        }

        // Desktop rotation logic remains unchanged
        const cameraToIntersection = new THREE.Vector3().subVectors(
            this.targetPoint,
            this.camera.position
        );
        
        const cameraToPlayer = new THREE.Vector3().subVectors(
            this.player.position,
            this.camera.position
        );
        
        cameraToPlayer.normalize();
        cameraToIntersection.normalize();
        const dotProduct = cameraToPlayer.dot(cameraToIntersection);
        
        const playerToIntersection = new THREE.Vector3().subVectors(
            this.targetPoint,
            this.player.position
        );

        const isCursorBehindPlayer = dotProduct > 0.95 || playerToIntersection.dot(cameraToPlayer) < 0;
        
        if (isCursorBehindPlayer) {
            return;
        }
        
        const direction = new THREE.Vector3().subVectors(
            this.targetPoint, 
            this.player.position
        );
        
        // Calculate target rotation
        this.targetRotation.y = Math.atan2(direction.x, direction.z);
        
        // Apply rotation with smoothing
        const rotationDiff = this.targetRotation.y - this.player.rotation.y;
        if (Math.abs(rotationDiff) > 0.01) {
            this.player.rotation.y += rotationDiff * this.rotationSpeed * deltaTime;
            this.player.modelMesh.rotation.y = this.player.rotation.y;
        }
    }
    
    /**
     * Update camera position to follow player
     * @private
     */
    _updateCamera() {
        if (!this.camera || !this.player || !this.player.modelMesh) return;
        
        const playerPosition = this.player.position.clone();
        
        // Smoothly update camera offset
        this._currentCameraOffset.lerp(this.cameraOffset, 0.1);
        
        // Calculate target camera position with consistent offset
        this._targetCameraPosition.set(
            playerPosition.x + Math.sin(this.player.rotation.y) * -this._currentCameraOffset.z,
            playerPosition.y + this._currentCameraOffset.y,
            playerPosition.z + Math.cos(this.player.rotation.y) * -this._currentCameraOffset.z
        );
        
        // Smoothly interpolate camera position with fixed speed
        this.camera.position.lerp(this._targetCameraPosition, 0.1);
        
        // Calculate look at position (slightly above player)
        const lookAtPosition = new THREE.Vector3(
            playerPosition.x + this.cameraLookAtOffset.x,
            playerPosition.y + this.cameraLookAtOffset.y,
            playerPosition.z + this.cameraLookAtOffset.z
        );
        
        // Initialize temporary target if needed
        if (!this._tempLookAtTarget) {
            this._tempLookAtTarget = lookAtPosition.clone();
        }
        
        // Smoothly move the temporary target toward the actual target
        this._tempLookAtTarget.lerp(lookAtPosition, 0.1); // Reduced from 0.2 for more stability
        
        // Always look at player
        this.camera.lookAt(this._tempLookAtTarget);
    }
    
    /**
     * Convert movement direction from camera space to world space
     * @param {THREE.Vector3} direction - Movement direction in camera space
     * @returns {THREE.Vector3} Movement direction in world space
     * @private
     */
    _cameraToWorldDirection(direction) {
        if (!this.camera) return direction.clone();
        
        // Get camera's forward and right vectors but project them onto the XZ plane
        const cameraForward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
        cameraForward.y = 0; // Remove vertical component
        cameraForward.normalize();
        
        const cameraRight = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
        cameraRight.y = 0; // Remove vertical component
        cameraRight.normalize();
        
        // Calculate world space movement
        const worldDirection = new THREE.Vector3();
        
        // Scale the forward/backward direction by the input
        if (direction.z !== 0) {
            worldDirection.addScaledVector(cameraForward, -direction.z);
        }
        
        // Scale the left/right direction by the input
        if (direction.x !== 0) {
            worldDirection.addScaledVector(cameraRight, direction.x);
        }
        
        // Normalize to ensure consistent speed in all directions
        if (worldDirection.length() > 0) {
            worldDirection.normalize();
        }
        
        return worldDirection;
    }
    
    /**
     * Update player movement
     * @param {number} deltaTime - Time since last update in seconds
     */
    update(deltaTime) {
        // Check if controller is enabled
        if (!this.enabled) {
            return;
        }
        
        // Debug player existence
        if (!this.player) {
            console.warn('MovementController update: player is null or undefined');
            return;
        }
        
        if (!this.camera || !this.scene) {
            console.warn('MovementController update: camera or scene is null');
            return;
        }
        
        if (!deltaTime) return;
        
        // Calculate movement based on input
        this._calculateMovement();
        
        // Only update if there's actual movement input or we need to update rotation
        if (this.player.modelMesh) {
            // Store previous position to check if we've moved
            const prevPosition = this.player.position.clone();
            const prevRotation = this.player.rotation.y;
            
            // Update player rotation regardless of movement
            this._updatePlayerRotation(deltaTime);
            
            // Apply movement if we have input
            if (this.movementDirection.length() > 0) {
                // Get the player's forward and right vectors based on current rotation
                const forward = new THREE.Vector3(0, 0, -1);
                const right = new THREE.Vector3(1, 0, 0);
                
                // Apply the player's current rotation
                forward.applyEuler(new THREE.Euler(0, this.player.rotation.y, 0));
                right.applyEuler(new THREE.Euler(0, this.player.rotation.y, 0));
                
                // Calculate the movement vector in world space
                const movement = new THREE.Vector3();
                
                // Apply forward/backward movement (remove negative sign to fix inversion)
                if (this.movementDirection.z !== 0) {
                    movement.addScaledVector(forward, this.movementDirection.z);
                }
                
                // Apply left/right movement (add negative sign to fix inversion)
                if (this.movementDirection.x !== 0) {
                    movement.addScaledVector(right, -this.movementDirection.x);
                }
                
                // Scale by speed and delta time
                if (movement.length() > 0) {
                    // Apply sprint multiplier if sprinting
                    const sprintMultiplier = this.keys.shift ? 2.0 : 1.0;
                    movement.normalize().multiplyScalar(this.maxMovementSpeed * deltaTime * sprintMultiplier);
                    
                    // Set move direction for animations (based on raw input)
                    this.player.moveDirection = {
                        forward: this.movementDirection.z,  // Remove negative sign
                        right: -this.movementDirection.x    // Add negative sign
                    };
                    
                    // Set the player's isMoving flag for animations
                    this.player.isMoving = true;
                    
                    // Set sprint flag based on shift key (no stamina check)
                    this.player.isSprinting = this.keys.shift;
                    
                    // Apply movement directly to player position
                    this.player.modelMesh.position.add(movement);
                    this.player.position.copy(this.player.modelMesh.position);
                }
            } else {
                // No movement input
                this.player.moveDirection = { forward: 0, right: 0 };
                this.player.isMoving = false;
                this.player.isSprinting = false;
            }
            
            // Update camera to follow player
            this._updateCamera();
            
            // Check if we should send position update to server
            const hasPositionChanged = !prevPosition.equals(this.player.position);
            const hasRotationChanged = prevRotation !== this.player.rotation.y;
            const now = Date.now();
            
            // Send position update if movement occurred or enough time has passed since last update
            if ((hasPositionChanged || hasRotationChanged) && 
                now - this.lastSyncTime >= this.syncRate && this.socket) {
                this._sendPositionUpdate();
                this.lastSyncTime = now;
            }
        }
    }
    
    /**
     * Send player position to server for syncing with other clients
     * @private
     */
    _sendPositionUpdate() {
        if (!this.socket || !this.player || !this.player.modelMesh) return;
        
        // Store player transform
        const position = this.player.modelMesh.position;
        const rotation = this.player.modelMesh.rotation;
        
        // Add current position to buffer
        this.positionBuffer.push({
            position: position.clone(),
            rotation: rotation.clone(),
            timestamp: Date.now()
        });
        
        // Keep buffer size limited
        if (this.positionBuffer.length > this.maxBufferSize) {
            this.positionBuffer.shift();
        }
        
        // Calculate velocity if we have enough data points
        let velocity = new THREE.Vector3();
        if (this.positionBuffer.length >= 2) {
            const lastPos = this.positionBuffer[this.positionBuffer.length - 2].position;
            const currentPos = this.positionBuffer[this.positionBuffer.length - 1].position;
            const timeDiff = (this.positionBuffer[this.positionBuffer.length - 1].timestamp - 
                            this.positionBuffer[this.positionBuffer.length - 2].timestamp) / 1000;
            
            if (timeDiff > 0) {
                velocity.subVectors(currentPos, lastPos).divideScalar(timeDiff);
            }
        }
        
        // Determine if player is moving for animation state
        const isMoving = this.player.isMoving;
        
        // Get attack state for animation synchronization
        const isAttacking = this.player.isAttacking || false;
        
        // Send transform data to server with velocity
        this.socket.emit('player-move', {
            position: {
                x: position.x,
                y: position.y,
                z: position.z
            },
            rotation: {
                y: rotation.y
            },
            velocity: {
                x: velocity.x,
                y: velocity.y,
                z: velocity.z
            },
            isMoving: isMoving,
            characterType: this.player.constructor.name,
            username: this.player.name,
            isAttacking: isAttacking,
            currentAnimation: this.player.currentAnimation || 'idle',
            attackStartTime: this.player.attackStartTime || 0
        });
    }
} 